import React, { Component } from 'react';
import {
  Row, Col, Form, Select, Input, Radio, DatePicker, Button, Icon, Tooltip,
} from 'antd';
import autobind from 'react-autobind';
import PropTypes from 'prop-types';
import moment from 'moment';
import { formPropType } from 'utils/propType';
import { FormControl, FormRules, FormUtils } from 'form-control';
import translate from 'app/translations';
import { getApiUrl } from 'app/apiClient';
import {
  PROFILE_STEP_TYPES, CREDENTIALS_DOC_TYPE, US_STATES, CREDENTIALS_STATUS, MAX_CREDENTIALS_LICENSE_COUNT,
} from 'app/constants';
import AgentProfilePropType from '../AgentProfilePropType';
import WizardNav from './WizardNav';
import DocumentTable from './DocumentTable';
import CredentialStatus from './CredentialStatus';
import FileUpload from '../../form-control/FileUpload';

import './Credentials.scss';

const RadioGroup = Radio.Group;
const { Option } = Select;

const APPRAISER_PHOTO = 'APPRAISER_PHOTO';
const W9 = 'W9';

export class CredentialsComponent extends Component {
  constructor(props) {
    super(props);
    autobind(this);
    this.licenseDocumentRef = undefined;
    this.w9Ref = undefined;
    this.appraiserPhotoRef = undefined;
    this.t = translate;
    this.state = {
      isSubmitted: false,
    };
  }

  onUploadDocument(documentReference) {
    documentReference.handleUpload()
      .then((documentResponse) => {
        this.props.form.resetFields();
        this.props.onSetCredentialDocuments(documentResponse.data.documents);
      })
      .finally(() => {
        this.setState({
          isSubmitted: false,
        });
      });
  }

  onUploadLicense() {
    const { form } = this.props;
    this.setState({
      isSubmitted: true,
    });

    form.validateFieldsAndScroll((errors) => {
      if (!errors) {
        const fileUploadErrors = this.licenseDocumentRef.validateFiles();

        if (errors || fileUploadErrors) {
          return {
            errors,
            fileUploadErrors,
          };
        }

        this.onUploadDocument(this.licenseDocumentRef);
      }
      return false;
    });
  }

  onSubmit(nextPage) {
    this.setState({
      isSubmitted: true,
    });

    const {
      credentialProgress,
    } = this.props.agentProfile;

    if (credentialProgress.credentialsStatus === CREDENTIALS_STATUS.SUCCESS) {
      this.props.onSetAgentProfileStepRequest(nextPage);
    } else {
      window.scrollTo(0, 0);
    }
  }

  render() {
    const {
      form,
    } = this.props;

    const {
      agentProfile,
    } = this.props;

    const {
      credentials, accessToken, applicationId, credentialProgress, constants, isCallInprogress,
    } = agentProfile;


    const {
      hasW9,
      hasProfilePhoto,
      hasMaxLicenseCount,
    } = credentialProgress.section;

    const {
      LICENSE,
      DOCUMENT,
    } = constants;

    const yesNoRadio = [
      { label: this.t('Credentials.active'), value: 'Yes' },
      { label: this.t('Credentials.inactive'), value: 'No' },
    ];


    const { isSubmitted } = this.state;

    const hasDocs = (credentials.documents && credentials.documents.length > 0);

    return (
      <div className="credentials">
        {isSubmitted && (
          <CredentialStatus
            agentProfile={agentProfile}
          />
        )}
        <div>
          <div className="card-container responsive-border">
            <Tooltip
              placement="top"
              title={<div style={{ maxWidth: 100 }}>{this.t('Credentials.pdfType')}</div>}
              trigger="click"
            >
              <h2 className="orange-text inline-block pointer">{this.t('Credentials.licenses')} <Icon className="blue-text" type="file-pdf" /></h2>
            </Tooltip>
            {hasMaxLicenseCount && (
              <p>
                {this.t('Credentials.maxLicenseWarn', { maxCount: MAX_CREDENTIALS_LICENSE_COUNT })}
              </p>
            )}
            {!hasMaxLicenseCount && (
              <div>
                <p>{this.t('Credentials.licenseInfo')}</p>
                <Form layout="vertical">
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <FormControl
                        form={form}
                        fieldName="type"
                        displayName={this.t('Credentials.licenseType')}
                        initialValue={credentials.type || undefined}
                        rules={[FormRules.REQUIRED]}
                      >
                        <Select
                          showSearch
                          autoComplete="off"
                          filterOption={(userInput, option) => FormUtils.filterOptions(userInput, option)}
                          placeholder={this.t('Credentials.licenseType')}
                        >
                          {
                            LICENSE.map(s => (<Option value={s.value} key={s.value}>{s.name}</Option>))
                          }
                        </Select>
                      </FormControl>
                    </Col>
                    <Col xs={24} md={8}>
                      <FormControl
                        form={form}
                        fieldName="state"
                        displayName={this.t('Credentials.state')}
                        initialValue={credentials.state || undefined}
                        rules={[FormRules.REQUIRED]}
                      >
                        <Select
                          showSearch
                          autoComplete="off"
                          filterOption={(userInput, option) => FormUtils.filterOptions(userInput, option)}
                          placeholder={this.t('Credentials.state')}
                        >
                          {
                            US_STATES.map(s => (<Option value={s.value} key={s.value}>{s.name}</Option>))
                          }
                        </Select>
                      </FormControl>
                    </Col>
                    <Col xs={24} md={8}>
                      <FormControl
                        form={form}
                        fieldName="licenseNumber"
                        displayName={this.t('Credentials.licenseNumber')}
                        initialValue={credentials.licenseNumber}
                        rules={[FormRules.REQUIRED]}
                      >
                        <Input
                          maxLength={25}
                          autoComplete="off"
                          placeholder={this.t('Credentials.licenseNumber')}
                        />
                      </FormControl>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={this.t('Credentials.initialDateOfLicensure')}
                      >
                        {form.getFieldDecorator('issuedDate', {
                          rules: [{
                            type: 'object',
                            required: true,
                            message: this.t('FormControl.validate.required',
                              { fieldName: this.t('Credentials.initialDateOfLicensure') }),
                          }],
                          initialValue: credentials.issueDate ? moment(credentials.issueDate, 'YYYY-MM-DD') : undefined,
                        })(
                          <DatePicker
                            format="YYYY-MM-DD"
                            disabledDate={FormUtils.futureDates}
                            className="full-width"
                          />,
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={this.t('Credentials.expiryDate')}
                      >
                        {form.getFieldDecorator('expiryDate', {
                          rules: [{
                            type: 'object',
                            required: true,
                            message: this.t('FormControl.validate.required',
                              { fieldName: this.t('Credentials.expiryDate') }),
                          }],
                          initialValue: credentials.expiryDate ? moment(credentials.expiryDate, 'YYYY-MM-DD') : undefined,
                        })(
                          <DatePicker
                            format="YYYY-MM-DD"
                            disabledDate={FormUtils.pastDates}
                            className="full-width"
                          />,
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <FormControl
                        form={form}
                        fieldName="active"
                        displayName={this.t('Credentials.isActive')}
                        initialValue={FormUtils.booleanToYesNo(credentials.isActive) || undefined}
                        validateTrigger="onChange"
                        rules={[FormRules.REQUIRED]}
                      >
                        <RadioGroup
                          className="correct-align-radio-buttons"
                          options={yesNoRadio}
                        />
                      </FormControl>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <FileUpload
                        displayName={this.t('Credentials.uploadLicenseDocument')}
                        isSubmitted={isSubmitted}
                        errorMsg={this.t('FormControl.validate.required', { fieldName: this.t('Credentials.uploadLicenseDocument') })}
                        required
                        metaData={{
                          type: CREDENTIALS_DOC_TYPE.LICENSE,
                          licenseInfo: {
                            ...form.getFieldsValue(),
                            active: FormUtils.yesNoToBoolean(form.getFieldValue('active')),
                          },
                        }}
                        ref={(c) => { this.licenseDocumentRef = c; }}
                        endpoint={`${getApiUrl()}/applications/${applicationId}/uploads?accessToken=${accessToken}`}
                        accept=".pdf"
                      />

                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} className="align-right">
                      <Button
                        type="primary"
                        htmlType="submit"
                        id="submitLicense"
                        onClick={this.onUploadLicense}
                      >
                        <Icon type="cloud-upload" />
                        {this.t('Generic.upload')}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            )}
          </div>
        </div>

        {!hasW9 && (
          <div className="card-container responsive-border">
            <Tooltip
              placement="top"
              title={<div style={{ maxWidth: 100 }}>{this.t('Credentials.pdfType')}</div>}
              trigger="click"
            >
              <h2 className="orange-text inline-block pointer">{this.t('Credentials.w9File')} <Icon className="blue-text" type="file-pdf" /></h2>
            </Tooltip>
            <p>{this.t('Credentials.w9Help')}</p>
            <FileUpload
              displayName={this.t('Credentials.w9File')}
              isSubmitted={isSubmitted}
              errorMsg={!hasW9 ? this.t('FormControl.validate.required', { fieldName: this.t('Credentials.w9File') }) : ''}
              required={!hasW9}
              ref={(c) => { this.w9Ref = c; }}
              endpoint={`${getApiUrl()}/applications/${applicationId}/uploads?accessToken=${accessToken}`}
              metaData={{ type: W9 }}
              accept=".pdf"
            />
            <Row gutter={16}>
              <Col xs={24} className="align-right">
                <Button
                  type="primary"
                  htmlType="submit"
                  id="submitLicense"
                  onClick={() => this.onUploadDocument(this.w9Ref)}
                >
                  <Icon type="cloud-upload" />
                  {this.t('Generic.upload')}
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {!hasProfilePhoto && (
          <div className="card-container responsive-border">
            <Tooltip
              placement="top"
              title={<div style={{ maxWidth: 150 }}>{this.t('Credentials.imgType')}</div>}
              trigger="click"
            >
              <h2 className="orange-text inline-block pointer margin-zero">{this.t('Credentials.appraiserPhotoFile')} <Icon className="blue-text" type="picture" /></h2>
            </Tooltip>
            <div className="m-b-16"><strong>{this.t('Credentials.optional')}</strong></div>
            <p>{this.t('Credentials.appraiserPhotoDescription')}</p>
            <p>{this.t('Credentials.appraiserPhotoPleaseUpload')}</p>
            <ul>
              <li>{this.t('Credentials.appraiserPhotoTips.hats')}</li>
              <li>{this.t('Credentials.appraiserPhotoTips.face')}</li>
              <li>{this.t('Credentials.appraiserPhotoTips.chest')}</li>
              <li>{this.t('Credentials.appraiserPhotoTips.group')}</li>
              <li>{this.t('Credentials.appraiserPhotoTips.driver')}</li>
            </ul>
            <FileUpload
              ref={(c) => { this.appraiserPhotoRef = c; }}
              endpoint={`${getApiUrl()}/applications/${applicationId}/uploads?accessToken=${accessToken}`}
              metaData={{ type: APPRAISER_PHOTO }}
              accept="image/*"
            />
            <Row gutter={16}>
              <Col xs={24} className="align-right">
                <Button
                  type="primary"
                  htmlType="submit"
                  id="submitLicense"
                  onClick={() => this.onUploadDocument(this.appraiserPhotoRef)}
                >
                  <Icon type="cloud-upload" />
                  {this.t('Generic.upload')}
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {hasDocs && (
          <div className="m-b-16">
            <DocumentTable
              agentProfile={agentProfile}
              documentList={credentials.documents || []}
              documentTypes={DOCUMENT}
              onDeleteDocument={this.props.onDeleteCredentialDocumentRequest}
            />
          </div>
        )}
        <WizardNav
          agentProfile={this.props.agentProfile}
          isCallInProgress={isCallInprogress}
          currentPage={PROFILE_STEP_TYPES.CREDENTIALS}
          onNavigate={this.onSubmit}
          showDivider={!hasDocs}
        />
      </div>
    );
  }
}

CredentialsComponent.propTypes = {
  agentProfile: PropTypes.shape(AgentProfilePropType).isRequired,
  form: PropTypes.shape(formPropType).isRequired,
  onSetCredentialDocuments: PropTypes.func.isRequired,
  onSetAgentProfileStepRequest: PropTypes.func.isRequired,
  onDeleteCredentialDocumentRequest: PropTypes.func.isRequired,
};

export default Form.create()(CredentialsComponent);
